import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {InvalidGuid} from "@app/review/models";

import {deserialize, deserializeArray} from '@app/serializable';
import {User} from '@app/shared/user.service';
import {Rect} from "@shared/euclid";

import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';

export class Annotation {
    public id: string;
    public asset: string;
    public user: User;
    public parent: string;
    public created: Date;
    public modified: Date;
    public children: Annotation[];
    public comment: string = '';
    public timestamp: number;
    public drawing: string|Blob = '';
    public transform: Rect;
}

const RegExToUser = new RegExp(/\b|\w+|\b/);
const RegExFromUser = new RegExp(/@\w+\b/);

@Injectable({
    providedIn: 'root',
})
export class AnnotationService {
    private _annotationSubject: BehaviorSubject<Annotation[]>;
    private _annotations: Annotation[];
    private _loadAnnotationsSubject: Subject<string>;

    public annotations$: Observable<Annotation[]>;
    public comments$: Observable<Annotation[]>;
    public drawings$: Observable<Annotation[]>;

    constructor(private http: HttpClient) {
        this._annotationSubject = new BehaviorSubject<Annotation[]>([]);
        this._loadAnnotationsSubject = new Subject<string>();

        this.annotations$ = this._annotationSubject.asObservable()
            .pipe(
                map(annotations => annotations.filter(i => i.comment || i.drawing))
            );
        this.comments$ = this.annotations$
            .pipe(
                map(annotations => annotations.filter(i => Boolean(i.comment)))
            );
        this.drawings$ = this.annotations$
            .pipe(
                map(annotations => annotations.filter(i => Boolean(i.drawing)))
            );
        this._annotations = [];


        this._loadAnnotationsSubject
            .pipe(
                switchMap(id => this.http.get<Annotation[]>(`/api/annotation/?asset=${id}`)),
                map((annotations) => deserializeArray(Annotation, annotations))
            )
            .subscribe((annotations) => {
                this._annotations = annotations;
                this._annotationSubject.next(this._annotations);
            });
    }

    annotationsForAsset(assetId: string): void {
        if (assetId === InvalidGuid)
            return;
        this._loadAnnotationsSubject.next(assetId);
    }

    create(annotation: Annotation, notify: boolean = true): Observable<Annotation> {
        const formData = new FormData();
        formData.append('asset', annotation.asset);
        if (annotation.parent) {
            formData.append('parent', annotation.parent);
        }
        formData.append('comment', annotation.comment);
        formData.append('timestamp', annotation.timestamp.toString());
        formData.append('drawing', annotation.drawing);

        formData.append('notify', notify ? 'notify' : '');

        return this.http
            .post<Annotation>(`/api/annotation/`, formData)
            .pipe(
                map((annotation) => deserialize(Annotation, annotation)),
                tap(annotation => {
                    this._annotations.push(annotation);
                    this._annotationSubject.next(this._annotations);
                })
            );
    }

    delete(annotationId: string): Observable<null> {
        return this.http.delete<null>(`/api/annotation/${annotationId}/`);
    }

    edit(annotationId: string, comment: string, notify: boolean = true): Observable<Annotation> {
        const options = {
            comment,
            notify,
            withCredentials: true,
        };
        return this.http
            .patch<Annotation>(`/api/annotation/${annotationId}/`, options)
            .pipe(
                map((annotation) => deserialize(Annotation, annotation)),
                tap(annotation => {
                    const index = this._annotations.findIndex(i => i.id === annotation.id);
                    if (index >= 0) {
                        this._annotations[index] = annotation;
                    }
                    this._annotationSubject.next(this._annotations);
                })
            );
    }

    editImage(annotationId: string, blob: Blob, notify: boolean = true): Observable<Annotation> {
        const formData = new FormData();
        formData.append('drawing', blob || '');
        formData.append('notify', notify ? 'notify' : '');

        return this.http
            .patch<Annotation>(`/api/annotation/${annotationId}/`, formData)
            .pipe(
                map((annotation) => deserialize(Annotation, annotation)),
                tap(annotation => {
                    const index = this._annotations.findIndex(i => i.id === annotation.id);
                    if (index >= 0) {
                        this._annotations[index] = annotation;
                    }
                    this._annotationSubject.next(this._annotations);
                })
            );
    }

    getFromTimestamp(timestamp: number): Annotation {
        return this._annotations.find(i => i.timestamp === timestamp);
    }
}
